/**
 * Shared map helper utilities
 * Used by both locationMap.js and searchMap.js
 */

// --- Pure utilities ---

export function isValidCoord(lng, lat) {
    return Number.isFinite(lng)
        && Number.isFinite(lat)
        && lng >= -180
        && lng <= 180
        && lat >= -90
        && lat <= 90;
}

export function formatPrice(price) {
    if (!price) return '';
    return '$' + Math.round(price).toLocaleString();
}

export function getListingKey(listing) {
    return listing.guestyListingId || listing.id;
}

/**
 * Get the best available price for a listing.
 * searchMap passes its search store to get reactive date-based prices.
 * locationMap passes null to always fall back to basePrice.
 */
export function getListingDisplayPrice(listing, searchStore) {
    const listingId = String(getListingKey(listing));
    return searchStore?.results?.listingPrices?.[listingId]
        || listing.basePrice
        || listing.prices?.basePrice;
}

// --- Shared constants ---

export const COORD_GROUP_RADIUS_FT = 35; // listings within this distance are grouped into a single stacked marker
const COORD_GRID_SIZE = COORD_GROUP_RADIUS_FT / 364_000; // approx feet-per-degree conversion

export const POPUP_GAP = 8;
export const POPUP_PADDING = 10;
export const DEFAULT_POPUP_HEIGHT = 350;
export const DEFAULT_MARKER_HEIGHT = 38;

export const POPUP_BASE_OPTIONS = {
    closeButton: false,
    closeOnClick: false,
    maxWidth: 'none',
    className: 'search-map-popup',
};

// --- Coordinate grouping ---

/**
 * Snap coordinates to a grid based on COORD_GROUP_RADIUS_FT and return a string key.
 */
export function coordKey(lat, lng) {
    const snapLat = Math.round(lat / COORD_GRID_SIZE) * COORD_GRID_SIZE;
    const snapLng = Math.round(lng / COORD_GRID_SIZE) * COORD_GRID_SIZE;
    return `${snapLat},${snapLng}`;
}

/**
 * Group listings that share the same (or very close) coordinates.
 *
 * @param {Array} listings - Array of listing objects
 * @param {Function} toNumber - Conversion helper for lat/lng values
 * @returns {{ markerGroups: Map, listingToMarkerKey: Map }}
 *   markerGroups: Map<markerKey, { listings[], lat, lng }>
 *     - Singles use the listing key as markerKey
 *     - Groups use `group_${coordKey}` as markerKey
 *   listingToMarkerKey: Map<listingKey, markerKey> — reverse lookup for hover
 */
export function groupListingsByCoordinate(listings, toNumber) {
    // First pass: bucket by coordKey
    const buckets = new Map();
    for (const listing of listings) {
        const lat = toNumber(listing.lat);
        const lng = toNumber(listing.lng);
        if (!isValidCoord(lng, lat)) continue;

        const key = coordKey(lat, lng);
        if (!buckets.has(key)) {
            buckets.set(key, { listings: [], lat, lng });
        }
        buckets.get(key).listings.push(listing);
    }

    // Second pass: build markerGroups and reverse lookup
    const markerGroups = new Map();
    const listingToMarkerKey = new Map();

    for (const [key, bucket] of buckets) {
        if (bucket.listings.length === 1) {
            // Single listing — use its listing key as the marker key
            const listing = bucket.listings[0];
            const listingKey = getListingKey(listing);
            markerGroups.set(listingKey, bucket);
            listingToMarkerKey.set(listingKey, listingKey);
        } else {
            // Multiple listings at same coordinate — group marker
            const markerKey = `group_${key}`;
            markerGroups.set(markerKey, bucket);
            for (const listing of bucket.listings) {
                listingToMarkerKey.set(getListingKey(listing), markerKey);
            }
        }
    }

    return { markerGroups, listingToMarkerKey };
}

// --- Popup positioning ---

/**
 * Pre-calculate the best popup anchor based on available screen space.
 * Avoids the render-then-check-then-flip cycle that causes visual flashes.
 *
 * @param {Object} params
 * @param {mapboxgl.Map} params.map
 * @param {Array} params.coordinates - [lng, lat]
 * @param {number} params.markerHeight
 * @param {number} params.gap
 * @param {number} params.lastPopupHeight - Cached height from previous popup render
 * @returns {{ anchor: string, offset: number }}
 */
export function calculatePopupAnchor({ map, coordinates, markerHeight, gap, lastPopupHeight }) {
    const markerScreenPos = map.project(coordinates);
    const estimatedPopupHeight = lastPopupHeight || DEFAULT_POPUP_HEIGHT;
    const spaceAbove = markerScreenPos.y - markerHeight;
    const anchor = spaceAbove >= estimatedPopupHeight + gap ? 'bottom' : 'top';
    const offset = anchor === 'bottom' ? (markerHeight + gap) : gap;
    return { anchor, offset };
}

/**
 * Check if popup overflows the map container and flip its anchor if needed.
 * Returns whether a flip occurred and the measured popup height.
 *
 * @param {Object} params
 * @param {mapboxgl.Map} params.map
 * @param {mapboxgl.Popup} params.popup
 * @param {Array} params.coordinates - [lng, lat]
 * @param {number} params.gap
 * @param {number} params.markerHeight
 * @param {Function} params.setSuppressClose - Callback to toggle _suppressClose flag
 * @param {Function} [params.onPan] - Called before panning the map (e.g. to set programmaticMoveInProgress)
 * @returns {{ flipped: boolean, measuredHeight: number }}
 */
export function ensurePopupVisible({ map, popup, coordinates, gap, markerHeight, setSuppressClose, onPan }) {
    const popupEl = popup.getElement();
    if (!popupEl) return { flipped: false, measuredHeight: 0 };

    const mapRect = map.getContainer().getBoundingClientRect();
    const popupRect = popupEl.getBoundingClientRect();

    const measuredHeight = popupRect.height > 0 ? popupRect.height : 0;

    const currentAnchor = popup.options.anchor;
    let flipped = false;

    if (popupRect.top < mapRect.top + POPUP_PADDING && currentAnchor === 'bottom') {
        setSuppressClose(true);
        try {
            popup.remove();
            popup.options.anchor = 'top';
            popup.options.offset = gap;
            popup.setLngLat(coordinates).addTo(map);
        } finally {
            setSuppressClose(false);
        }
        flipped = true;
    } else if (popupRect.bottom > mapRect.bottom - POPUP_PADDING && currentAnchor === 'top') {
        setSuppressClose(true);
        try {
            popup.remove();
            popup.options.anchor = 'bottom';
            popup.options.offset = markerHeight + gap;
            popup.setLngLat(coordinates).addTo(map);
        } finally {
            setSuppressClose(false);
        }
        flipped = true;
    }

    if (flipped) {
        requestAnimationFrame(() => {
            if (popup.getElement()) {
                panPopupIntoView({ map, popup, onPan });
            }
        });
    } else {
        panPopupIntoView({ map, popup, onPan });
    }

    return { flipped, measuredHeight };
}

/**
 * Pan the map so the popup is fully visible within the container.
 *
 * @param {Object} params
 * @param {mapboxgl.Map} params.map
 * @param {mapboxgl.Popup} params.popup
 * @param {number} [params.padding=POPUP_PADDING]
 * @param {Function} [params.onPan] - Called when a pan is about to occur (e.g. to set programmaticMoveInProgress)
 */
export function panPopupIntoView({ map, popup, padding = POPUP_PADDING, onPan }) {
    const popupEl = popup.getElement();
    if (!popupEl) return;

    const mapRect = map.getContainer().getBoundingClientRect();
    const popupRect = popupEl.getBoundingClientRect();

    let dx = 0;
    let dy = 0;

    if (popupRect.top < mapRect.top + padding) {
        dy = popupRect.top - (mapRect.top + padding);
    } else if (popupRect.bottom > mapRect.bottom - padding) {
        dy = popupRect.bottom - (mapRect.bottom - padding);
    }

    if (popupRect.left < mapRect.left + padding) {
        dx = popupRect.left - (mapRect.left + padding);
    } else if (popupRect.right > mapRect.right - padding) {
        dx = popupRect.right - (mapRect.right - padding);
    }

    if (dx !== 0 || dy !== 0) {
        if (onPan) onPan();

        const currentCenter = map.getCenter();
        const currentPoint = map.project(currentCenter);
        const newCenter = map.unproject({
            x: currentPoint.x + dx,
            y: currentPoint.y + dy,
        });

        map.easeTo({
            center: newCenter,
            duration: 300,
        });
    }
}
